import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { AuthChangeEvent, Session, SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseBrowserClient } from './supabase-client.js';
import { apiClient } from '../lib/api-client.js';
import { AuthContext } from './use-auth.js';
import { recoveryRedirectUrl, type AuthActivationType } from './auth-activation.js';

export type AuthPhase =
  | 'BOOTING'
  | 'SIGNED_OUT'
  | 'AUTHENTICATED'
  | 'SESSION_EXPIRED'
  | 'ERROR';

export interface AuthState {
  phase: AuthPhase;
  session: Session | null;
  message?: string;
}

export interface AuthContextValue extends AuthState {
  login(email: string, password: string): Promise<void>;
  requestPasswordRecovery(email: string): Promise<void>;
  verifyActivation(tokenHash: string, type: AuthActivationType): Promise<boolean>;
  updatePassword(password: string): Promise<boolean>;
  logout(): Promise<void>;
  getAccessToken(): string | null;
  loginPending: boolean;
}

export type BrowserAuthClient = Pick<SupabaseClient, 'auth'>;

function authStateFor(event: AuthChangeEvent, session: Session | null): AuthState {
  if (session) return { phase: 'AUTHENTICATED', session };
  if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
    return { phase: 'SESSION_EXPIRED', session: null };
  }
  return { phase: 'SIGNED_OUT', session: null };
}

function loginErrorMessage(error: unknown): string {
  const status = typeof error === 'object' && error && 'status' in error ? error.status : undefined;
  if (status === 400 || status === 401) {
    return 'The email or password is incorrect.';
  }
  return 'Sign in is temporarily unavailable. Check your connection and try again.';
}

export function AuthProvider({
  children,
  client,
}: {
  children: ReactNode;
  client?: BrowserAuthClient;
}) {
  const authClient = client ?? getSupabaseBrowserClient();
  const [state, setState] = useState<AuthState>({ phase: 'BOOTING', session: null });
  const [loginPending, setLoginPending] = useState(false);
  const sessionRef = useRef<Session | null>(null);
  const loginPromiseRef = useRef<Promise<void> | null>(null);
  const recoveryPromiseRef = useRef<Promise<void> | null>(null);

  const applyState = useCallback((next: AuthState) => {
    sessionRef.current = next.session;
    setState(next);
  }, []);

  useEffect(() => {
    apiClient.setAccessTokenProvider(() => sessionRef.current?.access_token ?? null);
    return () => apiClient.setAccessTokenProvider(undefined);
  }, []);

  useEffect(() => {
    let active = true;
    let observedAuthEvent = false;
    const {
      data: { subscription },
    } = authClient.auth.onAuthStateChange((event, session) => {
      if (!active) return;
      observedAuthEvent = true;
      applyState(authStateFor(event, session));
    });

    void authClient.auth.getSession().then(({ data, error }) => {
      if (!active || observedAuthEvent) return;
      if (error) {
        applyState({
          phase: 'ERROR',
          session: null,
          message: 'The saved session could not be restored. Sign in again.',
        });
        return;
      }
      applyState(
        data.session
          ? { phase: 'AUTHENTICATED', session: data.session }
          : { phase: 'SIGNED_OUT', session: null }
      );
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, [applyState, authClient]);

  const login = useCallback(
    (email: string, password: string): Promise<void> => {
      if (loginPromiseRef.current) return loginPromiseRef.current;

      const attempt = (async () => {
        setLoginPending(true);
        const { data, error } = await authClient.auth.signInWithPassword({ email, password });
        if (error || !data.session) {
          applyState({ phase: 'SIGNED_OUT', session: null, message: loginErrorMessage(error) });
          return;
        }
        applyState({ phase: 'AUTHENTICATED', session: data.session });
      })().finally(() => {
        setLoginPending(false);
        loginPromiseRef.current = null;
      });

      loginPromiseRef.current = attempt;
      return attempt;
    },
    [applyState, authClient]
  );

  const logout = useCallback(async () => {
    applyState({ phase: 'SIGNED_OUT', session: null });
    const { error } = await authClient.auth.signOut({ scope: 'local' });
    if (error) {
      applyState({
        phase: 'SIGNED_OUT',
        session: null,
        message: 'Signed out locally. The remote sign-out request could not be confirmed.',
      });
    }
  }, [applyState, authClient]);

  const requestPasswordRecovery = useCallback(
    (email: string): Promise<void> => {
      if (recoveryPromiseRef.current) return recoveryPromiseRef.current;
      const attempt = authClient.auth
        .resetPasswordForEmail(email, { redirectTo: recoveryRedirectUrl(window.location.origin) })
        .then(() => undefined)
        .catch(() => undefined)
        .finally(() => {
          recoveryPromiseRef.current = null;
        });
      recoveryPromiseRef.current = attempt;
      return attempt;
    },
    [authClient]
  );

  const verifyActivation = useCallback(
    async (tokenHash: string, type: AuthActivationType): Promise<boolean> => {
      try {
        const { data, error } = await authClient.auth.verifyOtp({ token_hash: tokenHash, type });
        if (error || !data.session) return false;
        applyState({ phase: 'AUTHENTICATED', session: data.session });
        return true;
      } catch {
        return false;
      }
    },
    [applyState, authClient]
  );

  const updatePassword = useCallback(
    async (password: string): Promise<boolean> => {
      try {
        const { data, error } = await authClient.auth.updateUser({ password });
        return !error && Boolean(data.user);
      } catch {
        return false;
      }
    },
    [authClient]
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      login,
      requestPasswordRecovery,
      verifyActivation,
      updatePassword,
      logout,
      loginPending,
      getAccessToken: () => sessionRef.current?.access_token ?? null,
    }),
    [state, login, logout, requestPasswordRecovery, verifyActivation, updatePassword, loginPending]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
